import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, FileText, Users, HelpCircle, Phone, Info, Sun, Moon, Globe, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import BhumiLogo from './BhumiLogo';

const Navbar = ({ isDark = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSite();
  const { language, toggleLanguage, t } = useLanguage();
  const { isDark: darkMode, toggleTheme } = useTheme();

  const navLinks = [
    { path: '/guides', label: t('nav.guides'), icon: FileText },
    { path: '/services', label: t('nav.services'), icon: Users },
    { path: '/blog', label: language === 'bn' ? 'ব্লগ' : 'Blog', icon: BookOpen },
    { path: '/faq', label: t('nav.faq'), icon: HelpCircle },
    { path: '/contact', label: t('nav.contact'), icon: Phone },
    { path: '/about', label: t('nav.about'), icon: Info },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${isDark ? 'glass-dark' : 'bg-white/95 backdrop-blur-md border-b border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
            <BhumiLogo size="sm" showText={false} isDark={isDark} />
            <span className={`text-lg md:text-xl font-bold hidden sm:block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {settings.site_name || 'ভূমি'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace('/', '')}`}
                className={`px-3 xl:px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'bg-[#2E7D32] text-white'
                    : isDark
                    ? 'text-gray-300 hover:text-white hover:bg-white/10'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language & Theme Toggle + CTA Button */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`p-2 rounded-full transition-colors ${
                isDark || darkMode
                  ? 'text-gray-300 hover:text-white hover:bg-white/10'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title={language === 'bn' ? 'Switch to English' : 'বাংলায় দেখুন'}
            >
              <Globe size={20} />
              <span className="sr-only">{language === 'bn' ? 'EN' : 'বাং'}</span>
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`p-2 rounded-full transition-colors ${
                isDark || darkMode
                  ? 'text-gray-300 hover:text-white hover:bg-white/10'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title={darkMode ? 'Light Mode' : 'Dark Mode'}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            
            <Link to="/contact">
              <Button 
                data-testid="navbar-cta"
                className="bg-[#FBC02D] text-gray-900 hover:bg-[#F9A825] rounded-full px-6 font-semibold"
              >
                {t('nav.help')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 rounded-lg ${isDark ? 'text-white' : 'text-gray-900'}`}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className={`lg:hidden ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#2E7D32] text-white'
                    : isDark
                    ? 'text-gray-300 hover:bg-white/10'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <link.icon size={20} />
                {link.label}
              </Link>
            ))}
            {/* Mobile Language & Theme */}
            <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-white/10 mt-2">
              <button
                onClick={toggleLanguage}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg ${
                  isDark || darkMode
                    ? 'text-gray-300 hover:bg-white/10'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Globe size={20} />
                {language === 'bn' ? 'English' : 'বাংলা'}
              </button>
              <button
                onClick={toggleTheme}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg ${
                  isDark || darkMode
                    ? 'text-gray-300 hover:bg-white/10'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                {darkMode ? 'Light' : 'Dark'}
              </button>
            </div>
            
            <Link to="/contact" onClick={() => setIsOpen(false)}>
              <Button className="w-full mt-4 bg-[#FBC02D] text-gray-900 hover:bg-[#F9A825] rounded-full font-semibold">
                {t('nav.help')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
